import PropTypes from 'prop-types';
import { Component } from 'react';
import ModuleLayout from '/@/components/module-layout';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationCropProtectionPDF from '/@/components/recommendation-crop-protection-pdf';
import RecommendationInfo from '/@/components/recommendation-info';
import RecommendationShare from '/@/components/recommendation-share';
import { PrintContext, UnitsContext } from '/@/core/context/app';
import { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';
import { toHeroBrands } from '/@/views/recommendations/v2/pdf/adapter';
import getInformationModules from '../recommendation-detail/modules';
import styles from './styles.module.css';
import '/@/styles/index.css';
import { Button, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import Header from '/@/components/bayer/header';
import HeroHeader, { pdfHeroLogos } from '/@/components/bayer/hero-header';
import HeroBrandLogo from '/@/components/bayer/hero-header/hero-brand-logo';
import PageContainer from '/@/components/bayer/page-container';
import settings from '/@/core/settings';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { parseUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { getApplicationOptions, getApplicationTypeOptions } from '/@/store/crop-protection/selectors';
import type { Recommendation } from '/@/types/recommendation';
import { formatDate, isPhone } from '/@/utils';
import { getCrops } from '../../dashboard/hooks/crops';

const getRecommendationFieldInfo = (recommendation: Recommendation) => {
    const backupField = recommendation.backup?.field;
    const nestedBackupField = recommendation.backup?.backup?.field;
    const topLevelField = recommendation.field;

    const name =
        recommendation.fieldName ||
        (typeof topLevelField === 'object' && topLevelField?.name) ||
        (typeof backupField === 'object' && backupField?.name) ||
        (typeof nestedBackupField === 'object' && nestedBackupField?.name) ||
        '';

    const location =
        (typeof topLevelField === 'object' && topLevelField?.location) ||
        (typeof backupField === 'object' && backupField?.location) ||
        (typeof nestedBackupField === 'object' && nestedBackupField?.location) ||
        '';

    return { name, location };
};

const getCropProtectionApplicationMomentText = (
    recommendationKey: string,
    recoData?: {
        result?: { crop?: string; applicationMoment?: string; applicationType?: string };
    }
) => {
    if (recommendationKey === 'seedTreatmentRecommendation') {
        return _('Siembra');
    }

    if (recommendationKey === 'diseaseRecommendation') {
        return getApplicationTypeOptions()[recoData?.result?.applicationType]?.label || '';
    }

    return (
        getApplicationOptions()[recoData?.result?.applicationMoment]?.title({
            crop: recoData?.result?.crop,
        }) || ''
    );
};

const Card = ({ title, texts, textsSecondary }: { title: string; texts: string[]; textsSecondary?: string[] }) => (
    <Stack
        spacing={2}
        p={2}
        sx={{
            border: (t) => `1px solid ${t.palette.divider}`,
            height: '100%',
            wordWrap: 'break-word',
        }}
    >
        <Typography variant='h6' color='primary'>
            {title}
        </Typography>
        <Stack spacing={0.5}>
            {texts.map((text) => (
                <Typography key={text} variant='body1' color='primary'>
                    {text}
                </Typography>
            ))}
            {textsSecondary?.map((text) => (
                <Typography key={text} variant='subtitle2' color='primary.mui.inactive'>
                    {text}
                </Typography>
            ))}
        </Stack>
    </Stack>
);

export class RecommendationPDFContent extends Component {
    static contextTypes = {
        history: PropTypes.object,
    };

    static childContextTypes = {
        print: PropTypes.bool,
        units: PropTypes.object,
        store: PropTypes.object,
    };

    state = {
        commentsDrawerOpen: false,
        showCommentsButton: false,
    };

    getChildContext() {
        return {
            print: true,
            units: this.getUnits(),
            store: {
                getState: () => ({
                    session: {
                        user: {},
                    },
                }),
                dispatch: () => {},
                subscribe: () => {},
            },
        };
    }

    getUnits() {
        return new UnitConverter({
            ...this.props.units,
            language: this.props.language,
            brands: this.props.appInstance.brands.reduce(
                (brandsByType, brand) => ({
                    ...brandsByType,
                    [brand.type]: brand,
                }),
                {}
            ),
        });
    }

    shouldRenderMapValues(field) {
        // Check if new field has the needed data
        try {
            return !!field.mapValues?.[0]?.map;
        } catch (e) {
            return false;
        }
    }

    render() {
        const recommendation = this.props.recommendation as Recommendation;
        return (
            <PrintContext.Provider value={true}>
                <UnitsContext.Provider value={this.getUnits()}>
                    {recommendation.crops.map((crop) => this.renderCrop(crop))}
                    {(recommendation.weedV2?.id ||
                        recommendation.diseaseRecommendation?.id ||
                        recommendation.plagueRecommendation?.id ||
                        recommendation.seedTreatmentRecommendation?.id) &&
                        this.renderCropProtectionRecommendation()}
                    {recommendation.id && (
                        <RecommendationCommentsDrawer
                            open={this.state.commentsDrawerOpen}
                            setCommentsDrawerOpen={(status) => this.setState({ commentsDrawerOpen: status })}
                            recommendationId={recommendation.id}
                        />
                    )}
                </UnitsContext.Provider>
            </PrintContext.Provider>
        );
    }

    renderCrop(cropConfig) {
        const { appInstance, recommendation, userNode, isPdf, user, source } = this.props;
        const isFieldview = source === 'fieldview' || settings.app.theme === 'fieldview';
        const brand = recommendation.brand || recommendation.crops[0].productRecommendations[0].profile.product.brand;
        const { country } = recommendation.field;
        // A recommendation can mix brands, so the hero carries one logo per brand its products
        // are on.
        const heroBrands = toHeroBrands(cropConfig, brand);
        const modules = getInformationModules({
            appInstance,
            recommendation,
            cropConfig,
            brand,
            country,
            productRecommendations: cropConfig.productRecommendations,
            pestControlPlans: cropConfig.pestControlPlans,
            isPdf: true,
            catalogBrands: cropConfig.catalogBrands,
        });

        const units = this.getUnits();

        const parsedUserNode = parseUserNode(userNode, user);

        return (
            <PageContainer
                key={cropConfig.crop}
                heroHeader={
                    isPdf ? (
                        <HeroHeader
                            title={_('Recomendación de híbrido de {{crop}}', {
                                crop: getCrops()[cropConfig.crop]?.name,
                            })}
                            body={_(
                                `${_('Productos sugeridos según el ambiente del lote y el rendimiento objetivo.')} ${
                                    recommendation?.updated
                                        ? _('Última actualización {{date}}', {
                                              date: formatDate(new Date(recommendation?.updated)),
                                          })
                                        : ''
                                }`
                            )}
                            background={settings.app.theme}
                            isFieldview={isFieldview}
                            actions={
                                <Stack direction='row' spacing={2}>
                                    {heroBrands.map((heroBrand) => (
                                        <HeroBrandLogo key={heroBrand.id ?? heroBrand.name} brand={heroBrand} />
                                    ))}
                                    {isFieldview ? (
                                        <img src={pdfHeroLogos.fieldview} alt={'FieldView'} height='40px' />
                                    ) : (
                                        settings.app.theme === 'expertobayer' && (
                                            <>
                                                <img
                                                    src={pdfHeroLogos.expertobayer}
                                                    alt={'Experto Bayer'}
                                                    height='40px'
                                                />
                                                <img src={pdfHeroLogos.bayer} alt={'Bayer'} height='40px' />
                                            </>
                                        )
                                    )}
                                </Stack>
                            }
                        />
                    ) : (
                        <Header
                            variant='full-gray'
                            title={_('Recomendación de híbrido de {{crop}}', {
                                crop: getCrops()[cropConfig.crop]?.name,
                            })}
                            subtitle={`${_('Productos sugeridos según el ambiente del lote y el rendimiento objetivo.')} ${
                                recommendation?.updated
                                    ? _('Última actualización {{date}} {{time}}', {
                                          date: formatDate(new Date(recommendation?.updated)),
                                          time: new Date(recommendation?.updated).toLocaleTimeString(),
                                      })
                                    : ''
                            }`}
                            actions={[
                                <RecommendationShare
                                    key='recommendation-share'
                                    type={cropConfig.crop}
                                    recommendation={{ ...recommendation, ...recommendation?.backup }}
                                    disabled={!recommendation}
                                    version={2}
                                    entrySource='recommendation-detail'
                                />,
                                this.props.showCommentsButton &&
                                    (this.props.isSmOrLower ? (
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            fullWidth
                                            onClick={() => this.setState({ commentsDrawerOpen: true })}
                                        >
                                            {_('Ver comentarios')}
                                        </Button>
                                    ) : (
                                        <RecommendationCommentsButton
                                            key='comments-button'
                                            commentsStatus={recommendation.commentsStatus}
                                            onOpenDrawer={() => this.setState({ commentsDrawerOpen: true })}
                                        />
                                    )),
                            ].filter(Boolean)}
                            breadcrumbs={this.props.breadcrumbs || []}
                        />
                    )
                }
            >
                <Grid2
                    className='mp-mask'
                    spacing={2}
                    container
                    columns={10}
                    justifyContent='space-evenly'
                    sx={{ width: '100%' }}
                    px={4}
                    py={4}
                >
                    <Grid2 sm={5} md={2} sx={{ height: 'auto' }}>
                        <Card
                            title={_('Productor')}
                            texts={[parsedUserNode.name]}
                            textsSecondary={[parsedUserNode.email]}
                        />
                    </Grid2>
                    <Grid2 sm={5} md={2} sx={{ height: 'auto' }}>
                        <Card
                            title={_('Lote')}
                            texts={[recommendation.field.name]}
                            textsSecondary={[recommendation.field.location]}
                        />
                    </Grid2>
                    <Grid2 sm={3} md={2} sx={{ height: 'auto' }}>
                        <Card
                            title={_('Fecha de siembra')}
                            texts={[formatDate(new Date(recommendation.crops[0].plantingDate))]}
                        />
                    </Grid2>
                    <Grid2 sm={4} md={2} sx={{ height: 'auto' }}>
                        <Card
                            title={_('Ambiente')}
                            texts={[units.toYield(cropConfig.environment, cropConfig.crop, 2).toString()]}
                        />
                    </Grid2>
                    <Grid2 sm={3} md={2} sx={{ height: 'auto' }}>
                        <Card
                            title={_('Híbridos')}
                            texts={cropConfig.productRecommendations
                                .map((p) => p?.product?.catalogName)
                                .filter(Boolean)}
                        />
                    </Grid2>
                </Grid2>
                <ModuleLayout
                    sidebar={undefined}
                    menu={
                        !isPhone() && !(this.context.print || this.props.isPdf) ? (
                            <RecommendationInfo
                                inline
                                brand={brand}
                                cropConfig={cropConfig}
                                productRecommendations={cropConfig.productRecommendations}
                                recommendation={recommendation}
                                actions={<RecommendationShare type={cropConfig.crop} recommendation={recommendation} />}
                                useUnitProviderProps={{
                                    units: this.props.units,
                                    language: this.props.language,
                                    brands: this.props.appInstance.brands,
                                }}
                                isPdf={this.context.print || this.props.isPdf}
                            />
                        ) : undefined
                    }
                    actions={
                        isPhone() ? (
                            <RecommendationShare type={cropConfig.crop} recommendation={recommendation} />
                        ) : undefined
                    }
                    modules={modules}
                    sidebarOnTop
                    className={styles.mainInner}
                />
            </PageContainer>
        );
    }

    renderCropProtectionRecommendation() {
        const { user, recommendation, language, userNode, source } = this.props;
        const isFieldview = source === 'fieldview' || settings.app.theme === 'fieldview';
        const recommendationKey = recommendation?.weedV2
            ? 'weedV2'
            : recommendation?.diseaseRecommendation
              ? 'diseaseRecommendation'
              : recommendation?.seedTreatmentRecommendation
                ? 'seedTreatmentRecommendation'
                : 'plagueRecommendation';

        const titleMapping = {
            weedV2: _('Recomendación de herbicidas'),
            diseaseRecommendation: _('Recomendación de fungicidas'),
            plagueRecommendation: _('Recomandación de insecticidas'),
            seedTreatmentRecommendation: _('Recomendación de tratamiento de semillas'),
        };

        const parsedUserNode = parseUserNode(userNode, user);

        const recoData = recommendation[recommendationKey];
        const { name: fieldName, location: fieldLocation } = getRecommendationFieldInfo(recommendation);

        return (
            <PageContainer
                heroHeader={
                    <Stack spacing={4}>
                        <HeroHeader
                            title={titleMapping[recommendationKey]}
                            body={`${_(
                                'Nuestras recomendaciones se basan en tus respuestas y en los estudios y tendencias regionales de nuestra base de datos.'
                            )} ${_('Última actualización {{date}}', {
                                date: formatDate(new Date(recommendation?.updated)),
                            })}`}
                            background={settings.app.theme}
                            isFieldview={isFieldview}
                            actions={
                                <Stack direction='row' spacing={2}>
                                    {isFieldview ? (
                                        <img src={pdfHeroLogos.fieldview} alt={'FieldView'} height='40px' />
                                    ) : (
                                        settings.app.theme === 'expertobayer' && (
                                            <>
                                                <img
                                                    src={pdfHeroLogos.expertobayer}
                                                    alt={'Experto Bayer'}
                                                    height='40px'
                                                />
                                                <img src={pdfHeroLogos.bayer} alt={'Bayer'} height='40px' />
                                            </>
                                        )
                                    )}
                                </Stack>
                            }
                        />
                        <Grid2
                            className='mp-mask'
                            spacing={2}
                            container
                            columns={recoData.plantingDate ? 10 : 8}
                            justifyContent='space-evenly'
                            sx={{ width: '100%' }}
                            px={2}
                            pb={4}
                        >
                            <Grid2 sm={5} md={2} sx={{ height: 'auto' }}>
                                <Card
                                    title={_('Productor')}
                                    texts={[parsedUserNode.name]}
                                    textsSecondary={[parsedUserNode.email]}
                                />
                            </Grid2>
                            <Grid2 sm={5} md={2} sx={{ height: 'auto' }}>
                                <Card title={_('Lote')} texts={[fieldName]} textsSecondary={[fieldLocation]} />
                            </Grid2>
                            <Grid2 sm={3} md={2} sx={{ height: 'auto' }}>
                                <Card title={_('Cultivo')} texts={[getCrops()[recoData.result.crop]?.name || '']} />
                            </Grid2>
                            {recoData.plantingDate && (
                                <Grid2 sm={4} md={2} sx={{ height: 'auto' }}>
                                    <Card
                                        title={_('Fecha de siembra')}
                                        texts={[formatDate(new Date(recoData.plantingDate))]}
                                    />
                                </Grid2>
                            )}
                            <Grid2 sm={3} md={2} sx={{ height: 'auto' }}>
                                <Card
                                    title={_('Momento de aplicación')}
                                    texts={[getCropProtectionApplicationMomentText(recommendationKey, recoData)]}
                                />
                            </Grid2>
                        </Grid2>
                    </Stack>
                }
            >
                <RecommendationCropProtectionPDF recommendation={recommendation} key={recommendation.id} />
            </PageContainer>
        );
    }
}

const RecommendationPDF = (props) => {
    const breadcrumbs = useBreadcrumbs('recommendation.RecommendationDetail');
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        props.recommendation?.commentsStatus,
        props.recommendation?.userNode
    );

    return (
        <RecommendationPDFContent
            {...props}
            breadcrumbs={breadcrumbs}
            isSmOrLower={isSmOrLower}
            showCommentsButton={shouldShowCommentsButton}
        />
    );
};

export default RecommendationPDF;
